/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {ResponseData} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import ExportProcess, {ExportProcessFields} from '../../../models/exportProcessModel';
import {AppContext} from '../../misc/appContext';
import {createErrorMessage} from '../../../util/utils';
import {trackPlatformAction} from '../../../util/analytics';
import {
    ActivateHandle,
    CustomerType,
    ExportNameAttr,
    ExportProcessStatus,
    Fulfilment,
    InvoiceExportType
} from '../../../util/interfaces';
import ExportInvoices from './exportInvoices';
import {onSetCrumbs} from '../../../util/events';
import {defaultInvoiceFields, defaultOrderFields} from '../../../util/exportHelper';
import ExportFieldsSelector from './exportFieldsSelector';
import Account from '../../../models/accountModel';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

function getInvoiceFields(account: Account | null): Array<string> {
    const storedFields = account?.getFlex(Account.INVOICE_FIELDS_FLEX);
    return storedFields?.length ? storedFields : defaultInvoiceFields;
}

function getOrderFields(account: Account | null): Array<string> {
    const storedFields = account?.getFlex(Account.ORDER_FIELDS_FLEX);
    return storedFields?.length ? storedFields : defaultOrderFields;
}

let loadTimeout: number | null = null;
const TIMEOUT = 1000;
export default function ExportInvoicesController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [exportProcess, setProcess] = useState(new ExportProcess({account}));
    const [exportProcesses, setProcesses] = useState(new Array<ExportProcess>());
    const [loadingCount, setLoadingCount] = useState(false);
    const [loadingProcesses, setLoadingProcesses] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [invoiceCount, setInvoiceCount] = useState(0);
    const [country, setCountry] = useState('');
    const [fulfilment, setFulfilment] = useState(Fulfilment.BOTH);
    const [nameAttr, setNameAttr] = useState(ExportNameAttr.ORDER_ID);
    const [reverseChargeOnly, setReverseChargeOnly] = useState(false);
    const [customerType, setCustomerType] = useState(CustomerType.BOTH);
    const [selectedInvoiceFields, setInvoiceFields] = useState<Array<string>>(getInvoiceFields(account));
    const [selectedOrderFields, setOrderFields] = useState<Array<string>>(getOrderFields(account));
    const fieldsRef = useRef<ActivateHandle>(null);

    function onClickFields() {
        fieldsRef.current?.activate(selectedInvoiceFields, selectedOrderFields);
    }

    function onSubmitFields(invoiceFields: Array<string>, orderFields: Array<string>) {
        setInvoiceFields(invoiceFields);
        setOrderFields(orderFields);
    }

    const onChange = (key: keyof ExportProcessFields) => (value: any) => {
        const process = exportProcess.clone();
        process.setField(key, value);
        setProcess(process);
    };

    function onChangeReverseCharge() {
        setReverseChargeOnly(!reverseChargeOnly);
    }

    function onChangeCountry(value: string) {
        setCountry(value);
    }

    function onChangeFulfilment(value: Fulfilment) {
        setFulfilment(value);
    }

    function onChangeNameAttr(value: ExportNameAttr) {
        setNameAttr(value);
    }

    function onChangeCustomerType(customerType: CustomerType) {
        setCustomerType(customerType);
    }

    const onDownloadProcess = (process: ExportProcess) => () => {
        if (process.status === ExportProcessStatus.FINISHED) {
            setLoadingProcesses(true);
            process.download(
                () => {
                    setLoadingProcesses(false);
                },
                (data: ResponseData) => {
                    setLoadingProcesses(false);
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t('settings.export.downloadFailedMsg', 'Het downloaden van het bestand is niet gelukt.')
                            ),
                        t('settings.export.downloadFailedTitle', 'Downloaden mislukt')
                    );
                }
            );
        }
    };

    function onSubmitProcess() {
        if (!validateValues()) return;
        setLoadingSubmit(true);
        exportProcess.export(
            (_: ExportProcess, _success: boolean, data: ResponseData) => {
                setLoadingSubmit(false);
                if (data.success) {
                    trackPlatformAction('settings.export', 'export_invoices');
                    showSnackbar(t('settings.export.exportCreating', 'De export wordt aangemaakt'));
                    loadProcesses();
                    window.scroll({top: document.body.scrollHeight, behavior: 'smooth'});
                    context.onChangeContext({account: new Account(data.account)});
                } else
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t('settings.export.createFailedMsg', 'Het aanmaken van de export is niet gelukt.')
                            ),
                        t('settings.export.createFailedTitle', 'Aanmaken mislukt')
                    );
            },
            {
                country,
                reverseChargeOnly,
                customerType,
                invoice_field_names: selectedInvoiceFields,
                order_field_names: selectedOrderFields,
                fulfilment
            },
            nameAttr
        );
    }

    function getInvoiceCount() {
        if (!exportProcess.fromDate || !exportProcess.toDate) return;
        setLoadingCount(true);
        exportProcess.getCount(
            (process: ExportProcess, success: boolean, data: ResponseData) => {
                setLoadingCount(false);
                if (data.success) setInvoiceCount(data.invoice_count);
                else
                    showDialog(
                        data.message ??
                            createErrorMessage(
                                t(
                                    'settings.export.loadCountFailedMsg',
                                    'Het laden van het aantal facturen is niet gelukt.'
                                )
                            ),
                        t('settings.export.loadFailedTitle', 'Laden mislukt')
                    );
            },
            {country, reverseChargeOnly, customerType, fulfilment}
        );
    }

    function validateValues(): boolean {
        const {toDate, fromDate, toDateMoment, fromDateMoment} = exportProcess;
        let error: string | null = null;
        if (!toDate)
            error = t(
                'settings.export.errFromDate',
                'Kies de datum vanaf wanneer de facturen geëxporteerd moeten worden.'
            );
        else if (!fromDate)
            error = t(
                'settings.export.errToDate',
                'Kies de datum tot en met wanneer de facturen geëxporteerd moeten worden.'
            );
        else if (toDateMoment!.isBefore(fromDateMoment!))
            error = t('settings.export.errToBeforeFrom', 'De Tot en met datum kan niet voor de Vanaf datum zijn.');
        if (error) showDialog(error, t('settings.export.errorTitle', 'Fout'));
        return error === null;
    }

    function loadProcesses(showLoad: boolean = true) {
        clearTimeout();
        if (!account) return;
        if (!loadingProcesses && showLoad) setLoadingProcesses(true);
        const data = {
            account_id: account.id,
            export_types: [
                InvoiceExportType.CSV,
                InvoiceExportType.PDF,
                InvoiceExportType.UBL,
                InvoiceExportType.COMBINED_PDF,
                InvoiceExportType.CSV_CUSTOM
            ]
        };
        ExportProcess.getAll(data, (processes: ExportProcess[], success: boolean, data: ResponseData) => {
            setLoadingProcesses(false);
            if (success) {
                setProcesses(processes);
                const hasActiveExport =
                    processes.filter((proc: ExportProcess) => proc.status === ExportProcessStatus.EXPORTING).length > 0;
                if (hasActiveExport)
                    loadTimeout = window.setTimeout(() => {
                        loadProcesses(false);
                    }, TIMEOUT);
            } else
                showDialog(
                    data.message ??
                        createErrorMessage(
                            t('settings.export.loadExportsFailedMsg', 'Het laden van de exports is niet gelukt.')
                        ),
                    t('settings.export.loadFailedTitle', 'Laden mislukt')
                );
        });
    }

    function clearTimeout() {
        if (loadTimeout !== null) {
            window.clearTimeout(loadTimeout);
            loadTimeout = null;
        }
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('settings.export.crumbSettings', 'Instellingen')},
            {label: t('settings.export.crumbExport', 'Exporteren')}
        ]);
        return () => {
            clearTimeout();
        };
    }, []);

    useEffect(() => {
        getInvoiceCount();
    }, [exportProcess, country, reverseChargeOnly, customerType, fulfilment]);

    useEffect(() => {
        loadProcesses();
    }, [account?.id]);

    return (
        <>
            <ExportInvoices
                onChange={onChange}
                onDownloadProcess={onDownloadProcess}
                onSubmitProcess={onSubmitProcess}
                onChangeCountry={onChangeCountry}
                onChangeNameAttr={onChangeNameAttr}
                onChangeReverseCharge={onChangeReverseCharge}
                onChangeCustomerType={onChangeCustomerType}
                onClickFields={onClickFields}
                onChangeFulfilment={onChangeFulfilment}
                exportProcess={exportProcess}
                nameAttr={nameAttr}
                exportProcesses={exportProcesses}
                loadingCount={loadingCount}
                loadingProcesses={loadingProcesses}
                loadingSubmit={loadingSubmit}
                invoiceCount={invoiceCount}
                country={country}
                reverseChargeOnly={reverseChargeOnly}
                customerType={customerType}
                fulfilment={fulfilment}
                selectedFieldsCount={selectedInvoiceFields.length + selectedOrderFields.length}
            />
            <ExportFieldsSelector onSubmitFields={onSubmitFields} ref={fieldsRef} />
        </>
    );
}
